import AsyncStorage from "@react-native-async-storage/async-storage";

const STORAGE_KEY = "book_recommendations_v1";
const ALLOWED_DEFAULT_IDS = new Set(["book_atomic_habits", "book_dopamine_nation"]);

/** @typedef {'default'|'curator'} BookSource */

/**
 * @typedef {Object} BookRecommendation
 * @property {string} id
 * @property {string} title
 * @property {string} author
 * @property {string} blurb
 * @property {string} [linkUrl]
 * @property {string} [coverUrl]
 * @property {boolean} featured
 * @property {BookSource} source
 * @property {string} createdAt
 */

/** @type {BookRecommendation[]} */
export const DEFAULT_BOOK_RECOMMENDATIONS = [
  {
    id: "book_atomic_habits",
    title: "Atomic Habits",
    author: "James Clear",
    blurb: "Small daily systems beat motivation — ideal for rebuilding discipline after a reset.",
    linkUrl: "https://jamesclear.com/atomic-habits",
    coverUrl: "https://covers.openlibrary.org/b/isbn/9780735211292-L.jpg",
    featured: true,
    source: "default",
    createdAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "book_dopamine_nation",
    title: "Dopamine Nation",
    author: "Anna Lembke",
    blurb: "Explains pleasure-pain balance and why modern overstimulation makes urges harder to resist.",
    linkUrl: "https://www.annalembke.com/dopamine-nation",
    coverUrl: "https://covers.openlibrary.org/b/isbn/9781524746728-L.jpg",
    featured: true,
    source: "default",
    createdAt: "2026-01-01T00:00:00.000Z",
  },
];

export function newBookId() {
  return `book_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * @param {unknown} raw
 * @returns {BookRecommendation[]}
 */
function normalizeBooks(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((b) => b && typeof b === "object")
    .map((b) => ({
      id: String(b.id || newBookId()),
      title: String(b.title || "Untitled").trim().slice(0, 120),
      author: String(b.author || "Unknown").trim().slice(0, 80),
      blurb: String(b.blurb || "").trim().slice(0, 500),
      linkUrl: typeof b.linkUrl === "string" && b.linkUrl.trim() ? b.linkUrl.trim().slice(0, 500) : undefined,
      coverUrl: typeof b.coverUrl === "string" && b.coverUrl.trim() ? b.coverUrl.trim().slice(0, 500) : undefined,
      featured: !!b.featured,
      source: b.source === "curator" ? "curator" : "default",
      createdAt: typeof b.createdAt === "string" ? b.createdAt : new Date().toISOString(),
    }))
    .filter((b) => b.source === "curator" || ALLOWED_DEFAULT_IDS.has(b.id));
}

/**
 * @param {BookRecommendation[]} books
 */
export function sortBooks(books) {
  return [...books].sort((a, b) => {
    if (a.featured !== b.featured) return a.featured ? -1 : 1;
    if (a.source !== b.source) return a.source === "curator" ? -1 : 1;
    return a.title.localeCompare(b.title);
  });
}

/** @returns {Promise<BookRecommendation[]>} */
export async function loadBookRecommendations() {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seeded = [...DEFAULT_BOOK_RECOMMENDATIONS];
      await saveBookRecommendations(seeded);
      return seeded;
    }
    const parsed = normalizeBooks(JSON.parse(raw));
    const merged = mergeMissingDefaults(parsed);
    // Persist trim so old books disappear from storage
    await saveBookRecommendations(merged);
    return merged;
  } catch {
    return [...DEFAULT_BOOK_RECOMMENDATIONS];
  }
}

/**
 * @param {BookRecommendation[]} books
 */
function mergeMissingDefaults(books) {
  const map = new Map(books.map((b) => [b.id, b]));
  for (const d of DEFAULT_BOOK_RECOMMENDATIONS) {
    if (!map.has(d.id)) {
      map.set(d.id, d);
    } else {
      // Refresh cover/link metadata for built-in books.
      const existing = map.get(d.id);
      map.set(d.id, {
        ...existing,
        coverUrl: d.coverUrl || existing.coverUrl,
        linkUrl: d.linkUrl || existing.linkUrl,
        title: d.title,
        author: d.author,
        blurb: existing.blurb || d.blurb,
        source: "default",
      });
    }
  }
  for (const id of [...map.keys()]) {
    const book = map.get(id);
    if (book?.source === "default" && !ALLOWED_DEFAULT_IDS.has(id)) map.delete(id);
  }
  return sortBooks(Array.from(map.values()));
}

/** @param {BookRecommendation[]} books */
export async function saveBookRecommendations(books) {
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(sortBooks(books)));
}

/**
 * @param {BookRecommendation[]} books
 * @param {Omit<BookRecommendation, 'id'|'source'|'createdAt'> & { featured?: boolean }} input
 */
export async function addCuratorBook(books, input) {
  const book = {
    id: newBookId(),
    title: input.title.trim(),
    author: input.author.trim() || "Unknown",
    blurb: input.blurb.trim(),
    linkUrl: input.linkUrl?.trim() || undefined,
    featured: input.featured !== false,
    source: /** @type {const} */ ("curator"),
    createdAt: new Date().toISOString(),
  };
  const next = sortBooks([...books, book]);
  await saveBookRecommendations(next);
  return next;
}

/**
 * @param {BookRecommendation[]} books
 * @param {string} id
 */
export async function removeBook(books, id) {
  const target = books.find((b) => b.id === id);
  if (!target || target.source === "default") return books;
  const next = books.filter((b) => b.id !== id);
  await saveBookRecommendations(next);
  return next;
}

/**
 * @param {BookRecommendation[]} books
 * @param {string} id
 * @param {boolean} featured
 */
export async function setBookFeatured(books, id, featured) {
  const next = books.map((b) => (b.id === id ? { ...b, featured } : b));
  await saveBookRecommendations(next);
  return sortBooks(next);
}

/** @param {BookRecommendation[]} books */
export function getTopFeaturedBooks(books, limit = 3) {
  return sortBooks(books).filter((b) => b.featured).slice(0, limit);
}
